import { createTheme, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Provider } from 'react-redux';
import { AxiosProvider } from 'src/common/api/AxiosContext';
import { useUserInfoState } from 'src/common/hooks/states/useUserInfoState';
import { initLanguage } from 'src/common/utils/Language';
import StorageHelper from 'src/common/utils/StorageHelper';
import { ReselectSeatModal } from 'src/components';
import { store } from 'src/redux/Store';
import { AppColors } from 'src/themes/Colors';
import GlobalStyle from 'src/themes/GlobalStyle';
import { metrics } from 'src/themes/Metrics';

import AppNavigation from './src/navigations/AppNavigation';

global.isDummy = false;

const App = () => {
  const { setUserInfo } = useUserInfoState();
  const queryClient = new QueryClient();

  useEffect(() => {
    // remove after release;
    if (!window?.nham24?.request) {
      StorageHelper.saveToken(
        // 010627192
        // 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6IjJlYjM2MmQxZTViNGNiNjkwOWY5NGU2MzRiMjZmOGUzOGMyMWJlNjllNTYxMTQyOTZhMjgzMzY1YWUwYzY0NmQ0NGNjNTllMTc3ZDE2NmY1In0.eyJhdWQiOiIyIiwianRpIjoiMmViMzYyZDFlNWI0Y2I2OTA5Zjk0ZTYzNGIyNmY4ZTM4YzIxYmU2OWU1NjExNDI5NmEyODMzNjVhZTBjNjQ2ZDQ0Y2M1OWUxNzdkMTY2ZjUiLCJpYXQiOjE2Nzk1NDM3MjgsIm5iZiI6MTY3OTU0MzcyOCwiZXhwIjoxNjgwODM5NzI4LCJzdWIiOiIxMTU0ODIiLCJzY29wZXMiOltdLCJpZCI6MTE1NDgyLCJwcm9pdmVyIjoidXNlcnMiLCJvcmlnaW4iOiJodHRwczpcL1wvZ29kZXYubmhhbTI0LmNvbSJ9.olSN98dc9liC42dloaH8VSpL0R5Ry0JE9KPqwRwBgEOlupapVYYPPq2EB6IfEDHmNo2OB322wZECfNssl0Qbh7n5V3GmyFuuhCHX9h8_VXAno3-KyuUZ8K2M6Uj3VSgiP8jvq0Dh_Cile6fkd4paaw-Wp3i4xjFb2ICR2fvLj14LblRupdFnZP49_7W9IXJwHz5QQgtAZm-TEVdhnJGsTRDQ7e_PW7x5sR9qOAv91GzGvf7rAewi_cL9ZVgxyUoNVnpiFtBnADLcsJtfMycOz8SDjnGa51YnJyC8Iqphx58d4GiglOf5C2afR4VWRguwzL_Erxp3hq6mYCXK4lsMiR3QP3EvGxRxHoOAhJRK0GWgc162NTLUM7aIBlg3BGjJIBC4MBo_lrC8NDoCZN9tTX2Z1H4MA-EGoprOepZkFNUjsu5Mz2cOlVG7Hx77ND-Hib_cwKuB76hGaeyrKnjxDptaeyknwBD1XhUrJ0olVjaler-bLJbTsL6OtGMRbnK1JmVwHA04UFwu9oICuYmD6OZ3msApOU3Bwkj-Dj0GuSHmtQYzBvzDzyHvEVAq8ybi0rBhfwFwWM5k9UQVoaFE5g1rPK_mIg2KsonMrq9h1bL4oPcbnkNZx99UQeVwoWCU8XZRmQMefEmz9c5BO1ekYoWwRdQ5mju6AZxgW9nuIzs',
        //
        // 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6ImZjZTUxYzlkMzhlZDY5OTUwMjkxYTY1ZTZkNDEyNTU5YmJlZjk3MWZiZWM0YzEzN2RhNWE0NzdhZWY1ODA5NTk4NjYzMzQzY2Q2ODZkNTNiIn0.eyJhdWQiOiIyIiwianRpIjoiZmNlNTFjOWQzOGVkNjk5NTAyOTFhNjVlNmQ0MTI1NTliYmVmOTcxZmJlYzRjMTM3ZGE1YTQ3N2FlZjU4MDk1OTg2NjMzNDNjZDY4NmQ1M2IiLCJpYXQiOjE2Nzg2NzkyOTMsIm5iZiI6MTY3ODY3OTI5MywiZXhwIjoxNjc5OTc1MjkzLCJzdWIiOiI3NSIsInNjb3BlcyI6W10sImlkIjo3NSwicHJvaXZlciI6InVzZXJzIiwib3JpZ2luIjoiaHR0cHM6XC9cL2dvZGV2Lm5oYW0yNC5jb20ifQ.MTxWDwZyVPLhQiAFTFHlP2RFVP-aGUisNaEWEHYlWGO3pfip9eFPxmrxmmS_cahWFb2cWS-Nb4HlNLKcu9-8_Zs-FyFO8OR0nw5YYqk73QXByRhJs4g-E8b6MRNuqT0m-vjSW4qaAqBxkKLbiD9K52zoP6uFd8KEbhJXzLGc80VHtEYu4EWXe1_Ed24C5Wj3rf1eIz-JKbgDIkY76MgFXFm3j8WljsOFa8zpHPtZPhZi7dMDHjnuKTSMlVe0p_59PJA3-FJorjZci2AfrnR-Kihe-B4jUSVr4vyBT1WT0rmWaWtwkX4VscKVU33gbcP2lfpxsO3JzTcW6h7DkROgiZ5LoU0l8T34rCloFPqOmDlZ-VALwYESsXSLTndRJC3p5VNf17Nx28xPxtRklULATqNMFhTdq4aHj_lg7bnFPGrf6KJGrGpN4_iIu3rLTA9M0vL5R_NsspPLCJ2xnDsoB2PBMqITlKn353atr2coMDPS6IAs5mlnTyAYibSPIN5ln8q4BDcy28-ZgrZC79dc_EPoljpfidtUKmVQKlInfuMnGhyYbkwdQqH_XrkUjQ6_B-W0LU9SxtLS5JcrQDwRSorVFIcEos7SdmPIjZWqNKzpzUWa271pr22shSGi1ij0b7effI50wz8wTn01QhOaAec93WiQRBBEkUABinxQ9Cc',
        // 010383629
        'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6IjNhZDJjOTE3MWE3MWE5Y2QzY2FiNDYxMDIyOWVlYTgxNzI2OGQ0ZTdlMmU0NWI2N2RiZWY5NzI1NTE3OWNiYzVhZTI1OGRhNDFkMjMxM2NjIn0.eyJhdWQiOiIyIiwianRpIjoiM2FkMmM5MTcxYTcxYTljZDNjYWI0NjEwMjI5ZWVhODE3MjY4ZDRlN2UyZTQ1YjY3ZGJlZjk3MjU1MTc5Y2JjNWFlMjU4ZGE0MWQyMzEzY2MiLCJpYXQiOjE2Nzk2NDAyMzIsIm5iZiI6MTY3OTY0MDIzMiwiZXhwIjoxNjgwOTM2MjMyLCJzdWIiOiI3NSIsInNjb3BlcyI6W10sImlkIjo3NSwicHJvaXZlciI6InVzZXJzIiwib3JpZ2luIjoiaHR0cHM6XC9cL2dvZGV2Lm5oYW0yNC5jb20ifQ.pab0DH-iIGEqcRpBaZSNjm09z5PCpHEQv791I38mJN2_7sUprdkrevwV37_LiOS4zvRg_3dmcC3u3aGVObBzst9wJUegvL02ENIVWkUbofpD82YNS5T5LJquq66kW4cBUEcF1zwvV795End6D_ggZYR1zzo1ltnsn1ua4hnVxJAQo6vZ9PU1u0kPFST8DI4Nll4cU0NBCNuJeQaL169w5AOPsXCqQzExmmN6OEoF8Tit1Ern7cg3801VDZFVqEmpHgyKo2a3UeSFD3IUBnE724-iPIQBUVhd6J7e27vfCjB_BQPCMh9oMiieF3jSmXEFx4uK4iXqU0P_Epro3pjhqMEzzWwhrg9aQgiSmLDWotdCFAzVjMYQxu0vywEKYootX66cMotC_xl_VtOF62rLvgxrK-mmuB4mQKSj8LSQJOAF0H52akzLJjV-7imqlzO6C_sf2ZwjrHyyQSeAJxbQbqedEk6f5le2L0unOs3oKnjpFvu9KvlbEJjfZ4WcwlFNXYAhwFeLE5iZz__yyKkmh9LrlhK4nmmzQxGc7Yy4ENMz-Hwx_Chh01UOj6PU4xzxINqt_JVnRmgyM08H9idg7WFxgvxFT3D6NMkY16ynXE_jeJ4JfXCEZ7AwBXyjuovJOvRnn9mUZt8o5rn1qEg4LJGQVUh04vuknrmMweoI6XY',
        // 070556458
        // 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6IjFiNjRiMmI1YTNlNWJmMTZhZjQ3OGVjZDY4MGUwYTE0ZWNhNjVlM2FmZTFiNDkxOGM4ZWQyZjY4NjZhODI0Mzk1NzczYjVkMDk0YzUyZjI2In0.eyJhdWQiOiIyIiwianRpIjoiMWI2NGIyYjVhM2U1YmYxNmFmNDc4ZWNkNjgwZTBhMTRlY2E2NWUzYWZlMWI0OTE4YzhlZDJmNjg2NmE4MjQzOTU3NzNiNWQwOTRjNTJmMjYiLCJpYXQiOjE2Nzk1NDQyMDUsIm5iZiI6MTY3OTU0NDIwNSwiZXhwIjoxNjgwODQwMjA1LCJzdWIiOiI0NDM0MzkiLCJzY29wZXMiOltdLCJpZCI6NDQzNDM5LCJwcm9pdmVyIjoidXNlcnMiLCJvcmlnaW4iOiJodHRwczpcL1wvZ29kZXYubmhhbTI0LmNvbSJ9.dE8n8gVb8oNwnbAFUESwKKrHIQDTRvqwy24lOqpur2WNULxeu-XekGCtJfahCErA-zGuyNMW9QL3Ok7A7NA1bhuMjyHTDPiFYOIaivc638nk9eXD8RvdnTF__s0Cat-J2QK6eYcHvM3rRDIhbKsUDX5c1GsRkANzR6ifL3ZlbpP6ws4h5nCmwWJdAzOkK6FezrFul7m7rptH_1L-z7acQhIDDePmtJ5wV3k2_OYwEGcMdu7IlAMk_jZUuL26XN_RIF0CTQos4NMA7JW79T1gKtjkAkRZHZP36W0q7vQheRlN8DFMsaths5GptUWmlqsrMrug9bLzXLbQC_XX4HSTIazdGbO8GoLu6x9Sh0Mp9-rhl-CudJLgGeN7GptsSd19Cy1juWMtJxjl3gDGnNR4IhpnDRrBXMs11p-dP3-4Di91Bn1IakpMS2fvB1ZIwgGIDdfmxcIv-JkUSGyJEyKx1yLPgVjqv7I-meEWE6Ztl1fYB4fhSOSpFhoyix6QtSu-sXeL3-pAuCjankg0Jha5N6I0LsS06iubkjU0sADJTkOmk1ABe8mDR2QGVhysnPG_p5W8BZ_xARoniEmYVDlIvw1-5s2XNiD4Vaxlk6UfPUJHd6R6i6PqLzD1yOgnJM4yL6tKK5sDfiVHEX1GGWpsKNiLE953Ihb-xhlUZxGG624',
        '75',
      );
      setUserInfo({
        access_token: '',
        email: '',
        full_name: 'hello',
        id: '75',
        phone: '+85512123123',
      });
    }

    window?.nham24?.request('request/user', {}).then(
      async (res: {
        data: {
          access_token: string;
          email: string;
          phone: string;
          phone_display: string;
          fullname: string;
          id: string;
        };
      }) => {
        const { access_token, email, fullname, id, phone_display } = res.data;

        window?.nham24?.request('consolelog', res.data);
        const response = await StorageHelper.getToken();

        if (response.id !== id) {
          // new user;
          await StorageHelper.clearAllData();
        }

        await StorageHelper.saveToken(res.data.access_token, id);

        setUserInfo({
          access_token,
          email,
          full_name: fullname,
          id,
          phone: phone_display.replaceAll(' ', ''),
        });
      },
    );

    // TestTime.getInstance().start();
    // const handlePuaseTimer = () => {
    //   setDuration(TestTime.getInstance().pause());
    // };

    // DeviceEventEmitter.addListener('PAUSE_TIMER', handlePuaseTimer);
    initLanguage();
  }, []);

  return (
    <Provider store={store}>
      <StatusBar backgroundColor="light-content" />
      <AxiosProvider>
        <QueryClientProvider client={queryClient}>
          <SafeAreaProvider>
            <ReselectSeatModal>
              <GlobalStyle css="html, body {overscroll-behavior: contain; overscroll-behavior-y: contain;} *:focus:not(.focus-visible) {outline: none;}" />
              <ThemeProvider theme={muiTheme}>
                <AppNavigation />
              </ThemeProvider>
            </ReselectSeatModal>
          </SafeAreaProvider>
        </QueryClientProvider>
      </AxiosProvider>
    </Provider>
  );
};

const muiTheme = createTheme({
  shape: {
    borderRadius: metrics.borderRadius,
  },
  palette: {
    primary: {
      main: AppColors.primary,
    },
    secondary: {
      main: AppColors.black,
    },
    info: {
      main: AppColors.blue,
    },
    divider: AppColors.border,
  },
  // components: {
  //   MuiInputLabel: {
  //     styleOverrides: {
  //       filled: {
  //         '&:not(.Mui-focused)': {
  //           color: AppColors.gray,
  //           transform: 'translate(14px,0px) scale(0.75)',
  //         },
  //       },
  //     },
  //   },
  // },
});

export default App;
